import { t } from 'state/intl/translations';
import {
  AppOffer,
  AppOfferGroup,
  AppOfferListItem,
  AppPersonalizedOfferGroup,
  IncentiveSource,
  LoyaltyEngineId,
  LoyaltyOffer,
  SanityId,
  appOfferToLoyaltyOffer,
  isAppOffer,
  isAppOfferGroup,
  isAppPersonalizedOfferGroup,
} from 'state/loyalty/types';
import { maybeLocaleImages } from 'utils/graphql';
import { TraceName, startTrace } from 'utils/performance/trace';
import { blockContentToPlainText } from 'utils/sanity';

import { AnimatedOfferItem } from '../loyalty-incentives-components/animated-offer-item';
import { parseIncentiveData } from '../loyalty-incentives-components/incentive-card/parse-incentive-data';
import { LoyaltyOfferGroupItem } from '../loyalty-incentives-components/loyalty-offer-group-item';
import { LoyaltyOffersItem } from '../loyalty-incentives-components/loyalty-offers-item';

/**
 * Given a list of offers, collect all endDate and expirationDate values,
 * discard those with year 9999, and return the closest date or undefined.
 */
const getClosestEndDate = (
  offers: readonly { endDate?: string | null; expirationDate?: string | null }[]
): string | undefined => {
  const allDates = offers
    .flatMap(offer => [offer?.endDate, offer?.expirationDate])
    .filter((date): date is string => !!date && date.slice(0, 4) !== '9999');

  return allDates.length > 0 ? allDates.sort()[0] : undefined;
};

export const renderOfferItem = ({
  item,
  handlePress,
  skipAnimation,
}: {
  item: AppOffer;
  handlePress: (offer: LoyaltyOffer) => void;
  skipAnimation: boolean;
}) => {
  const loyaltyOffer = appOfferToLoyaltyOffer(item);
  const incentiveData = parseIncentiveData(loyaltyOffer);
  const isPromoCode = (incentiveData.metadata || []).some(
    data => data?.value === IncentiveSource.PROMOTION_CODE
  );
  const _renderOfferItem = () => (
    <LoyaltyOffersItem
      handlePress={() => {
        startTrace(TraceName.OFFER_DETAIL_LOAD, { id: incentiveData.id, name: incentiveData.name });
        return handlePress(loyaltyOffer);
      }}
      incentiveData={incentiveData}
    />
  );
  // Animate only if skipAnimation is disabled and offer source is Promo Code campaing
  return !skipAnimation && isPromoCode ? (
    <AnimatedOfferItem setSkipAnimation={(skip: boolean) => (skipAnimation = skip)}>
      {_renderOfferItem()}
    </AnimatedOfferItem>
  ) : (
    _renderOfferItem()
  );
};

const renderOfferGroupItem = ({
  item,
  handlePress,
}: {
  item: AppOfferGroup;
  handlePress: (group: { cmsId: SanityId }) => void;
}) => {
  const image = maybeLocaleImages(item?.localizedImage);
  const name = blockContentToPlainText(item?.name?.localeRaw);
  const description = blockContentToPlainText(item?.description?.localeRaw);
  const endDate = getClosestEndDate(item.offers ?? []);

  return (
    <LoyaltyOfferGroupItem
      groupId={item.cmsId}
      name={name}
      description={description}
      image={image}
      imageDescription={description}
      handlePress={() => handlePress(item)}
      endDate={endDate}
    />
  );
};

const renderPersonalizedOfferGroupItem = ({
  item,
  handlePress,
}: {
  item: AppPersonalizedOfferGroup;
  handlePress: (group: { cmsId: SanityId; id?: LoyaltyEngineId }) => void;
}) => {
  // Personalized Offer Groups don't have native CMS content, but can optionally inherit header
  // content from their source Systemwide Offer Group (looked up by cmsId during reduceOffers).
  // If missing, render a safe fallback header.
  const image = item?.localizedImage ? maybeLocaleImages(item.localizedImage) : null;
  const name = item?.name ? blockContentToPlainText(item?.name?.localeRaw) : t('selectOffer');
  const description = item?.description
    ? blockContentToPlainText(item?.description?.localeRaw)
    : '';
  const endDate = getClosestEndDate(item.offers ?? []);

  return (
    <LoyaltyOfferGroupItem
      groupId={item.cmsId}
      name={name}
      description={description}
      image={image}
      imageDescription={description}
      handlePress={() => handlePress(item)}
      endDate={endDate}
    />
  );
};

export const LoyaltyListItem = ({
  item,
  handleOfferPress,
  handleGroupPress,
  skipAnimation,
}: {
  item: AppOfferListItem;
  handleOfferPress: (offer: LoyaltyOffer) => void;
  handleGroupPress: (group: { cmsId: SanityId; id?: LoyaltyEngineId }) => void;
  skipAnimation: boolean;
}) => {
  if (isAppPersonalizedOfferGroup(item)) {
    return renderPersonalizedOfferGroupItem({
      item,
      handlePress: handleGroupPress,
    });
  }
  if (isAppOfferGroup(item)) {
    return renderOfferGroupItem({ item, handlePress: handleGroupPress });
  } else if (isAppOffer(item)) {
    return renderOfferItem({ item, handlePress: handleOfferPress, skipAnimation });
  }
  return null;
};
